import { Food, INFOODS, Nutrients } from '@cf-shared/data-model/food';
import {
  addIngredientIntoMealTotal,
  Ingredient,
  isIngredientMacro,
  isIngredientReal,
  isUsableIngredientWeight,
  mergeUnusableNutrientInput,
  newUnusableNutrientInput,
  UnusableNutrientInput,
} from '@cf-shared/data-model/ingredient';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { RoundToDecimals } from '@cf-shared/shared-util';
import {
  describeIngredientForReport,
  NutrientDataQualityReporter,
  reportMissingNutrientMap,
  reportUnusableNutrientInput,
} from '../util/nutrient-data-quality-reporter';

/**
 * The arithmetic behind a meal's SAVED nutrition total (df-b21924615c5d).
 *
 * `FoodDbService.recalculateMealNutrientsAsync` reads each ingredient's food and then hands
 * everything to this function, which decides what the total contains. It lives here, apart
 * from the service, because the service pulls in PouchDB and the Angular DI graph and cannot
 * be loaded by the unit test runner -- so every rule about the saved total used to be pinned
 * only by tests reading the source as text, which can catch a deleted line but never a wrong
 * number. Here the rules are run: see `meal-nutrient-total.test.ts`.
 *
 * The total is written into the user's food log, so a wrong one is permanent. That is why
 * every ingredient that has to be left out is reported rather than dropped in silence.
 */

/**
 * Add up a meal from its ingredients and the foods already read for them.
 *
 * `foodsById` holds what the read returned for each real ingredient's food id: the food, or
 * `null` when it is genuinely gone. A read that FAILED must never reach this function -- the
 * caller has to stop before calling it, so a moment of bad connectivity is not saved as a
 * too-low total. An id missing from the map is treated as gone, and reported the same way.
 *
 * Also writes each ingredient's cached `kcal`, which the service has always done here.
 *
 * @returns the meal total, each nutrient rounded to 4 significant digits (`RoundToDecimals`
 *   uses `toPrecision`, so 1234.56 is stored as 1235)
 */
export function sumSavedMealNutrients(
  meal: Pick<Log_Meal, '_id' | 'ingredients'>,
  foodsById: ReadonlyMap<string, Food | null>,
  logging: NutrientDataQualityReporter,
): Nutrients {
  const nutrients: Nutrients = {};
  // one collector for the whole meal, reported once after it -- so a meal holding a damaged
  // value produces one Sentry event rather than one per ingredient that touched it
  const unusable = newUnusableNutrientInput();

  for (const [ingredientIndex, mealFood] of meal.ingredients.entries()) {
    if (isIngredientReal(mealFood)) {
      const food = foodsById.get(mealFood.foodId);
      if (!food) {
        // the food really is gone, so this ingredient can contribute nothing. The total we are
        // about to save is therefore short by whatever it held, which nothing else would report
        // -- the food read is reported inside getFoodByIdAsync, but that says nothing about a
        // meal total being written wrong. A stable name with the ids in the context keeps these
        // grouped as one Sentry issue instead of one per food.
        logging.trackSilentProblem('meal-nutrients: ingredient food is missing from the saved total', {
          mealId: meal._id,
          foodId: mealFood.foodId,
          ingredientWeight_g: mealFood.weight_g,
        });
        continue;
      }
      // a food record with no nutrient map at all -- the collector has no key to record it
      // against, so it is reported on its own and contributes nothing
      if (!food.nutrients) {
        reportNoStoredNutrients(logging, meal, mealFood);
        continue;
      }

      const source = describeIngredientForReport(mealFood, ingredientIndex);
      // if the WEIGHT is what is wrong, say so, rather than letting every nutrient the food
      // stores turn into NaN and be reported as a damaged stored value -- which is how a meal
      // logged with an empty amount field looked like a damaged food (df-6aedbb363d8c). The
      // arithmetic is left exactly as it was, so the saved total does not change; only the
      // per-nutrient records of this one ingredient go to a collector nobody reports.
      const weightIsUsable = isUsableIngredientWeight(mealFood.weight_g);
      if (!weightIsUsable) {
        recordUnusableIngredientWeight(unusable, mealFood.weight_g, source);
      }

      // what this food contributes at this weight -- the one statement of the rule, which
      // knows that a recipe scales by its own total weight and an ordinary food by 100 g
      const scaled = addIngredientIntoMealTotal(nutrients, mealFood, food, weightIsUsable ? unusable : newUnusableNutrientInput(), source);

      // cache food's calories TODO this should be cached locally, not inside database record
      mealFood.kcal = scaled[INFOODS.Calories];
    } else if (isIngredientMacro(mealFood)) {
      // as above, but here it is the ingredient itself that carries no nutrient map
      if (!mealFood.nutrients) {
        reportNoStoredNutrients(logging, meal, mealFood);
        continue;
      }

      // a typed-in ingredient stores its numbers per 100 g, so 100 is the reference weight.
      // `macroIngredientNutrientsForWeight` is the same rule with a guard on top, and it is
      // NOT used here: for a weight it cannot multiply by it hands the stored per-100 g
      // numbers back UNSCALED, which would write them into the user's log as though they
      // were what was eaten. Scaling by such a weight is the better of the two on a total
      // that is saved: short by this ingredient rather than wrong by a whole portion. Only
      // some of those weights are also VISIBLE, though -- `undefined` and NaN scale every
      // nutrient to NaN, which `addNutrientsInto` counts as zero and records in the
      // collector, while `null` scales them to zero and a negative weight scales them to
      // ordinary negative numbers, and neither of those is recorded by anything. Same limit
      // as the one `reportNoStoredNutrients` sets out below; do not read either as proof
      // that every unusable weight on this path is reported.
      const scaled = addIngredientIntoMealTotal(nutrients, mealFood, undefined, unusable, describeIngredientForReport(mealFood, ingredientIndex));

      // cache food's calories TODO this should be cached locally, not inside database record
      mealFood.kcal = scaled[INFOODS.Calories];
    }
  }

  // report whatever the scaling had to skip, once for the whole meal. Safe to call when
  // nothing was recorded, which is the normal case.
  reportUnusableNutrientInput(unusable, logging, 'meal-total.saved', { mealId: meal._id });

  // round all nutrients in the meal to sensible values
  for (const key of Object.keys(nutrients)) {
    nutrients[key] = RoundToDecimals(nutrients[key], 4);
  }
  return nutrients;
}

/**
 * Record in the meal's collector that a real ingredient's weight could not be used, naming
 * the ingredient, so the meal's one report says 'ingredient weight' (df-6aedbb363d8c).
 */
function recordUnusableIngredientWeight(mealUnusable: UnusableNutrientInput, weight_g: unknown, source: string) {
  const ingredientUnusable = newUnusableNutrientInput();
  ingredientUnusable.weightWasUnusable = true;
  ingredientUnusable.unusableWeight_g = weight_g;
  mergeUnusableNutrientInput(mealUnusable, ingredientUnusable, source);
}

/**
 * Report that there are no stored nutrients to add up for this ingredient.
 *
 * Two documents can produce it, which is why the wording names neither: for a real ingredient
 * the FOOD record it points at has no nutrient map (`IngredientReal` carries none of its own),
 * and for a typed-in ingredient it is the ingredient itself. Whoever triages this has to be
 * told which, so both ids go in the context.
 *
 * The reporting itself lives in `reportMissingNutrientMap` beside the collector's own report,
 * so the two halves of "the scaling rule was handed something it could not use" share one
 * Sentry name and one dedup.
 *
 * **It is not a complete guard, and must not be read as one.** A weight of `null` scales every
 * nutrient to zero rather than to NaN, so nothing is recorded and the ingredient still
 * contributes nothing in silence. That is the arithmetic this total has always had; it is
 * written down so the next reader does not take this report as proof that every damaged input
 * on this path is accounted for.
 */
function reportNoStoredNutrients(logging: NutrientDataQualityReporter, meal: Pick<Log_Meal, '_id'>, mealFood: Ingredient) {
  reportMissingNutrientMap(logging, 'meal-total.saved', {
    mealId: meal._id,
    foodId: isIngredientReal(mealFood) ? mealFood.foodId : undefined,
    ingredientWeight_g: mealFood.weight_g,
  });
}
